import { ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { GridColDef, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { DatasetAccess } from '~/api/types';
import {
  useGetDatasetAccessQuery,
  useGetDatasetQuery,
  useLazyGetDatasetAffectedProjectsQuery,
  useRevokeDatasetAccessMutation,
} from '~/api/v2/hooks';
import { DeleteTypeEnum, SearchTextField, Tooltip } from '~/components';
import { PAGE_SIZE, Table } from '~/components/Table';
import { tableActionButtonStyles } from '~/components/styles';
import { useDebounce } from '~/hooks';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { DATE_FORMAT } from '~/utils';
import AffectedProjectsPanel from './AffectedProjectsPanel';

const ManageDatasetAccessPanel = () => {
  const dispatch = useAppDispatch();

  const datasetId = +useParams().id!;
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });

  const { data: dataset } = useGetDatasetQuery({ datasetId });

  const [searchText, setSearchText] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortItem | null>(null);

  const { data, isFetching, isError } = useGetDatasetAccessQuery({
    datasetId,
    sort: sortModel?.field,
    order: sortModel?.sort,
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    search: useDebounce(searchText),
  });

  const [fetchAffectedProjects] = useLazyGetDatasetAffectedProjectsQuery();
  const [revokeAccess] = useRevokeDatasetAccessMutation();

  const handleRevokeAccess = (userId: string) => {
    revokeAccess({
      userId,
      datasetId,
      name: data?.data.find((user) => user.user_id === userId)?.name || '',
    });
  };

  const handleOpenRevokeModal = async (userId: string) => {
    let itemsCount: number;

    try {
      const affectedProjects = await fetchAffectedProjects({ datasetId, userId }).unwrap();
      itemsCount = affectedProjects.length;
    } catch {
      itemsCount = 0;
    }

    dispatch(
      openModal({
        type: ModalTypesEnum.DELETE_CONFIRMATION,
        payload: {
          type: DeleteTypeEnum.DATASET_ACCESS,
          onDelete: () => handleRevokeAccess(userId),
          content: <AffectedProjectsPanel userId={userId} />,
          itemsCount,
          deleteButtonLabel: 'Revoke',
          cancelButtonLabel: 'Cancel',
          isDangerStyle: false,
        },
      }),
    );
  };

  const columns: GridColDef<DatasetAccess>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'email',
      flex: 1.2,
      headerName: 'Email Id',
    },
    {
      field: 'created',
      flex: 0.8,
      headerName: 'Date of granted',
      renderCell: ({ row }) => format(row.created, DATE_FORMAT),
    },
    {
      field: 'deleted_at',
      flex: 0.8,
      headerName: 'Date of revoked',
      renderCell: ({ row }) => (row.deleted_at ? format(row.deleted_at, DATE_FORMAT) : '—'),
    },
    {
      field: 'actions',
      sortable: false,
      headerName: 'Actions',
      flex: 0.7,
      renderCell: ({ row }) => {
        const isDatasetUser =
          dataset?.data_owner_name === row?.user_id ||
          dataset?.secondary_data_owners?.includes(row?.user_id || '') ||
          false;

        return (
          <Tooltip title={isDatasetUser && 'Please remove the Steward from dataset in order to revoke the access'}>
            <Button
              disabled={!!row.deleted_at || isDatasetUser}
              sx={tableActionButtonStyles}
              onClick={() => handleOpenRevokeModal(row.user_id)}
            >
              Revoke
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const handleSortChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel[0] || null);
  };

  return (
    <Stack spacing={3} p={3}>
      <Typography variant="bodyXl">Manage dataset access</Typography>
      <Box sx={{ borderRadius: '2rem' }}>
        <SearchTextField
          size="small"
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          onClear={() => setSearchText('')}
          value={searchText}
          placeholder="Search"
        />
      </Box>
      <Table
        rows={isError ? [] : data?.data || []}
        columns={columns}
        loading={isFetching}
        rowCount={data?.pagination.total ?? 0}
        paginationModel={paginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={setPaginationModel}
        onSortChange={handleSortChange}
      />
    </Stack>
  );
};

export default ManageDatasetAccessPanel;
